import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs';

@Directive({
  selector: '[a360Icon]',
  standalone: false
})
export class IconDirective implements OnChanges, AfterViewInit {
  @Input() icon: string;
  @Input() fullIconPath: string;
  @Input() text: string;
  @Input() width: string;
  @Input() height: string;
  @Input() viewBox: string;
  @Input() isHoverEnable: boolean;
  @Input() color: string;
  @Input() hoverColor: string;

  private readonly nativeElement: HTMLElement;

  constructor(private element: ElementRef, private http: HttpClient, private renderer: Renderer2) {
    this.nativeElement = this.element.nativeElement;
    this.height = '20px';
    this.width = '20px';
    this.viewBox = 'auto';
    this.isHoverEnable = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.icon || changes.fullIconPath) {
      this.initSvg();
    } else {
      if (changes.text) {
        this.insertText();
      }

      if (changes.color) {
        this.nativeElement.classList.remove(changes.color.previousValue || '');
        this.nativeElement.classList.add(changes.color.currentValue || '');
      }

      if (changes.width || changes.height) {
        this.setStyle(this.nativeElement.querySelector('svg') as SVGElement);
      }
    }
  }

  ngAfterViewInit() {
    const svg = this.nativeElement.querySelector('svg');
    if (svg) {
      this.setViewBox(svg);
      this.setStyle(svg);
    }
  }

  @HostListener('mouseover') public setHoverColor() {
    if (this.isHoverEnable) {
      this.nativeElement.classList.remove(this.color);
      this.nativeElement.classList.add(this.hoverColor);
    }
  }

  @HostListener('mouseout') public setInitColor() {
    if (this.isHoverEnable) {
      this.nativeElement.classList.remove(this.hoverColor);
    }
    this.nativeElement.classList.add(this.color);
  }

  private getIconPath() {
    return this.fullIconPath || `../../../assets/icons/${this.icon}`;
  }

  private initSvg() {
    if (
      ['thumbs-up.svg', 'thumbs-up-unfilled.svg', 'thumbs-down.svg', 'thumbs-down-unfilled.svg'].includes(this.icon) ||
      this.fullIconPath !== undefined
    ) {
      const headers = new HttpHeaders().set('Accept', 'image/svg+xml');

      this.http
        .get(this.getIconPath(), { headers, responseType: 'text' })
        .pipe(
          map((svg) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(svg, 'image/svg+xml');
            return doc.querySelector('svg') as SVGElement;
          }),
          map(this.setViewBox.bind(this)),
          map(this.setStyle.bind(this))
        )
        .subscribe((svgElement: SVGElement) => {
          this.setInitColor();
          this.setIcon(svgElement);
          this.insertText();
        });
    } else {
      this.setInitColor();
      this.insertText();
    }
  }

  private setIcon(icon: SVGElement) {
    const iconWrapper = this.nativeElement.querySelector('.icon-wrapper');
    iconWrapper.innerHTML = '';

    iconWrapper.appendChild(icon);
  }

  private setViewBox(icon: SVGElement) {
    if (this.viewBox) {
      if (this.viewBox === 'auto') {
        const w = icon.getAttribute('width');
        const h = icon.getAttribute('height');
        if (h && w) {
          const vb = `0 0 ${w} ${h}`;
          this.renderer.setAttribute(icon, 'viewBox', vb);
          this.renderer.removeAttribute(icon, 'width');
          this.renderer.removeAttribute(icon, 'height');
        }
      } else if (this.viewBox !== '') {
        this.renderer.setAttribute(icon, 'viewBox', this.viewBox);
        this.renderer.removeAttribute(icon, 'width');
        this.renderer.removeAttribute(icon, 'height');
      }
    }

    return icon;
  }

  private setStyle(icon: SVGElement) {
    const style = `width: ${this.width}; height: ${this.height}`;
    this.renderer.setAttribute(icon, 'style', style);

    return icon;
  }

  private insertText() {
    if (this.text) {
      const span = this.nativeElement.querySelector('span') || document.createElement('span');
      span.textContent = this.text || '';
      span.classList.add('icon-text');
      this.nativeElement.appendChild(span);
    } else if (this.nativeElement.querySelector('span')) {
      this.nativeElement.querySelector('span').classList.remove('icon-text');
      this.nativeElement.removeChild(this.nativeElement.querySelector('span'));
    }
  }
}
